import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const DAYS = ["L", "M", "M", "J", "V", "S", "D"];

const fmt = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const todayStr = () => {
  const t = new Date();
  return fmt(t.getFullYear(), t.getMonth(), t.getDate());
};

export default function Availability() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [data, setData] = useState({}); // {date: {status, note}}
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: "Mariage", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/availability");
        const map = {};
        (res.data || []).forEach((d) => { map[d.date] = d; });
        setData(map);
      } catch (e) {
        console.error("Failed to load availability", e);
      }
    };
    load();
  }, []);

  const days = useMemo(() => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startDow = (first.getDay() + 6) % 7; // Monday-first
    const total = last.getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    return cells;
  }, [year, month]);

  const goPrev = () => {
    const nm = month === 0 ? 11 : month - 1;
    const ny = month === 0 ? year - 1 : year;
    setYear(ny); setMonth(nm);
  };
  const goNext = () => {
    const nm = month === 11 ? 0 : month + 1;
    const ny = month === 11 ? year + 1 : year;
    setYear(ny); setMonth(nm);
  };

  const handleSelect = (d) => {
    if (!d) return;
    const ds = fmt(year, month, d);
    if (ds < todayStr()) return;
    const status = data[ds]?.status;
    if (status === "booked") return;
    setSelected(ds);
    setSuccess(false);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/bookings", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        event_date: selected,
        event_type: form.event_type,
        message: form.message,
      });
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", event_type: "Mariage", message: "" });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const cellClass = (d) => {
    if (!d) return "invisible";
    const ds = fmt(year, month, d);
    const status = data[ds]?.status;
    const past = ds < todayStr();
    let cls = "day-cell";
    if (past) cls += " disabled";
    if (status === "available") cls += " available";
    if (status === "booked") cls += " booked";
    if (status === "option") cls += " option";
    if (selected === ds) cls += " selected";
    return cls;
  };

  return (
    <section id="disponibilites" className="relative py-28 lg:py-40 bg-[#070707]" data-testid="availability-section">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#3B82F6]/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="reveal flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-[#3B82F6]">Calendrier</div>
            <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[0.9]">
              Disponibilités<br />
              <span className="text-[#3B82F6]">en temps réel</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-3 text-xs uppercase tracking-[0.2em]">
            <Legend color="#22c55e" label="Disponible" />
            <Legend color="#f59e0b" label="Option" />
            <Legend color="#ef4444" label="Réservé" />
          </div>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-7 reveal glass-strong rounded-2xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <button onClick={goPrev} className="w-11 h-11 rounded-full border border-white/10 hover:border-[#3B82F6] hover:text-[#3B82F6] flex items-center justify-center transition-all" data-testid="calendar-prev">
                <ChevronLeft size={18} />
              </button>
              <div className="text-center">
                <div className="font-display text-3xl md:text-4xl">{MONTHS[month]}</div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mt-1">{year}</div>
              </div>
              <button onClick={goNext} className="w-11 h-11 rounded-full border border-white/10 hover:border-[#3B82F6] hover:text-[#3B82F6] flex items-center justify-center transition-all" data-testid="calendar-next">
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-2 mb-3">
              {DAYS.map((d, i) => (
                <div key={i} className="text-center text-[10px] uppercase tracking-[0.3em] text-zinc-500 py-1">{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-2">
              {days.map((d, i) => (
                <div
                  key={i}
                  className={cellClass(d)}
                  onClick={() => handleSelect(d)}
                  data-testid={d ? `calendar-day-${fmt(year, month, d)}` : undefined}
                >
                  {d}
                </div>
              ))}
            </div>
          </div>

          {/* Booking form */}
          <div className="lg:col-span-5 reveal glass-strong rounded-2xl p-6 md:p-8" data-testid="booking-form-card">
            <div className="flex items-center gap-3 mb-2">
              <CalendarIcon size={18} className="text-[#3B82F6]" />
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-300">Demande de réservation</div>
            </div>
            <div className="font-display text-2xl">{selected ? selected : "Sélectionnez une date"}</div>
            {selected && (
              <p className="text-sm text-zinc-400 mt-1">
                Statut actuel :{" "}
                <span className="text-white">
                  {data[selected]?.status === "available" ? "Disponible"
                    : data[selected]?.status === "option" ? "Sous option"
                    : data[selected]?.status === "booked" ? "Réservé"
                    : "Sur demande"}
                </span>
              </p>
            )}

            {success ? (
              <div className="mt-6 p-5 rounded-xl border border-[#3B82F6]/40 bg-[#3B82F6]/5 flex items-start gap-3" data-testid="booking-success">
                <CheckCircle2 className="text-[#3B82F6] shrink-0" />
                <div>
                  <div className="font-display text-lg">Demande envoyée</div>
                  <p className="text-sm text-zinc-300 mt-1">Anton vous recontacte sous 24h pour confirmer la date.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="booking-form">
                <Field label="Nom" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="booking-name" />
                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="booking-email" />
                <Field label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="booking-phone" />

                <div>
                  <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Type d'événement</label>
                  <select
                    value={form.event_type}
                    onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                    className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none transition-colors"
                    data-testid="booking-event-type"
                  >
                    {["Mariage", "Anniversaire", "Soirée privée", "Festival", "Nouvel an", "Entreprise", "Autre"].map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Message</label>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none transition-colors resize-none"
                    placeholder="Informations utiles, lieu, horaires…"
                    data-testid="booking-message"
                  />
                </div>

                {error && <div className="text-sm text-red-400" data-testid="booking-error">{error}</div>}

                <button
                  type="submit"
                  disabled={!selected || submitting}
                  className="btn-neon w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="booking-submit"
                >
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Envoi…</> : "Envoyer la demande"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-2 text-zinc-300">
      <span className="w-3 h-3 rounded-full" style={{ background: color, boxShadow: `0 0 12px ${color}` }} />
      {label}
    </span>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none transition-colors"
        data-testid={testid}
      />
    </div>
  );
}
